import { CssBaseline } from "@mui/material";
import { Navigate, redirect, RouterProvider } from "react-router";
import { createBrowserRouter } from "react-router-dom";
import GetCases from "./api/GetCases";
import Home from "./api/Home";
import {
  ParseCaseApiProvider,
  useCaseApi,
} from "./api/case/parse/ParseCaseClient";
import CaseCreator from "./components/CaseCreator.tsx";
import { CaseOverview } from "./components/CaseOverview";
import { CasePage } from "./components/CasePage";
import ErrorPage from "./components/ErrorPage";
import { Parties } from "./components/Parties";
import { RouterSkeleton } from "./components/RouterSkeleton";
import { Testimonials } from "./components/Testimonials.tsx";
import { UserProfile } from "./components/UserProfile.tsx";
import { AgreementsPage } from "./components/agreements/AgreementsPage.tsx";
import { AnalyticsGate } from "./components/analytics/AnalyticsGate";
import {
  ResultsSwitch,
  ResultsViewProvider,
  ShownResults,
} from "./components/analytics/ShownResults";
import { CaseOutcomes } from "./components/analytics/CaseOutcomes";
import { DecisionTree } from "./components/analytics/DecisionTree";
import { EvAnalytics } from "./components/analytics/EvAnalytics";
import { NetResultAnalytics } from "./components/analytics/NetResultAnalytics";
import { RevenueAnalytics } from "./components/analytics/RevenueAnalytics";
import { RiskAnalytics } from "./components/analytics/RiskAnalytics";
import { BargainingAnalytics } from "./components/analytics/settlement/BargainingAnalytics";
import { SettlementAnalytics } from "./components/analytics/settlement/SettlementAnalytics";
import { LoggedInPage } from "./components/auth/LoggedInPage";
import MfaEnrolment from "./components/auth/MfaEnrolment";
import { ParseAuthProvider, useAuth } from "./components/auth/ParseAuthContext";
import SignIn from "./components/auth/SignIn";
import { Budgeting } from "./components/costs/Budgeting";
import { CostApportionments } from "./components/costs/CostApportionments";
import { CostRecoveries } from "./components/costs/CostRecoveries";
import { LawFirmFees } from "./components/costs/LawFirmFees";
import { Claims } from "./components/issues/Claims";
import { ClientsPortfolio } from "./components/portfolio/ClientsPortfolio.tsx";
import { LawFirmPortfolio } from "./components/portfolio/LawFirmPortfolio.tsx";
import { PortfolioPage } from "./components/portfolio/PortfolioPage";
import SettlementsPortfolio from "./components/portfolio/SettlementsPortfolio.tsx";
import {
  CASE_ROUTE_ID,
  CaseContextProvider,
  CaseData,
} from "./context/CaseContext";
import { CurrencyContextProvider } from "./context/CurrencyContext.tsx";
import { ClaimsPortfolio } from "./components/portfolio/ClaimsPortfolio.tsx";
import { TimelinePage } from "./components/timeline/TimelinePage.tsx";
import { ReservePage } from "./components/reserve/ReservePage.tsx";
import { PersonaTerms } from "./context/usePersona.ts";
import {
  FeatureFlagsProvider,
  useFeatureFlags,
} from "./context/FeatureFlagsContext.tsx";
import { PortfolioProvider } from "./context/PortfolioContext";
import { ThemeContextProvider } from "./context/ThemeContext.tsx";
import { canWrite } from "./shared/AccessList.ts";
import { AgreementType, isAgreementType } from "./shared/AgreementType.ts";
import { PartyAgreements } from "./shared/Agreements.ts";
import { CaseCounts } from "./shared/CaseCounts.ts";
import {
  Claim,
  Damage,
  Configuration,
  CostStructure,
  Party,
  SettlementOffer,
} from "./shared/CaseModel";
import { CaseListElement } from "./shared/CasesList.ts";
import { Budget } from "./shared/CostModel";
import {
  PortfolioOverview,
  PortfolioStats,
  SettlementPortfolio,
} from "./shared/PortfolioTypes.ts";
import { isSettlementType, SettlementType } from "./shared/SettlementType.ts";
import { CaseFilter } from "./shared/parse/CasesQuery.ts";
import randomId from "./shared/randomId";
import { FrozenByJudgment } from "./components/timeline/FrozenByJudgment";

function AuthCheck() {
  // check login
  const { auth } = useAuth();
  const featureFlags = useFeatureFlags();

  if (auth.kind !== "signedIn") return <SignIn />;

  if (!auth.user.mfaEnabled) {
    // Whether the user is exempt from the second factor is the server's to
    // say; until it has, neither the enrolment screen nor the app is theirs.
    if (!featureFlags) return null;
    if (!featureFlags.mfaExempt) {
      return <MfaEnrolment username={auth.user.username} />;
    }
  }

  return (
    <ParseCaseApiProvider>
      <AppRoutes />
    </ParseCaseApiProvider>
  );
}

function getString(formData: FormData, key: string): string {
  const value = formData.get(key);
  if (typeof value !== "string") {
    throw `Expected '${key}' value '${value}' to be a string`;
  }
  return value;
}

/** What closing Trial or Enforcement saves along with the stage. */
function closingRecord(formData: FormData) {
  const read = (key: string) =>
    formData.has(key) ? JSON.parse(getString(formData, key)) : undefined;
  return { judgment: read("judgment"), closing: read("closing") };
}

export type PortfolioData = {
  caseCounts: CaseCounts;
  // cases matching current filter query
  stats: PortfolioStats;
};

/** What the start page loads. */
export type HomeData = {
  cases: CaseListElement[];
  // not awaited by the loader: the figures of the portfolio may take a sweep
  // of every case on the server, and the cases show without waiting for them
  overview: Promise<PortfolioOverview>;
};

/** What the routes that list the cases load. */
export type CasesData = {
  cases: CaseListElement[];
  // the names the access list of a case can know the user under: with them the
  // list tells the cases the user may only read (see canWrite)
  accessNames: string[];
};

type ActionArgs = {
  request: Request;
  params: Record<string, string | undefined>;
};

// Wraps route actions to silently handle aborted requests.
// When a useFetcher component unmounts during navigation, its in-flight action
// may be aborted. Without this wrapper the abort error reaches the errorElement.
function withAbortHandling(
  action: (args: ActionArgs) => Promise<unknown>,
): (args: ActionArgs) => Promise<unknown> {
  return async (args) => {
    try {
      return await action(args);
    } catch (error) {
      if (args.request.signal?.aborted) return null;
      throw error;
    }
  };
}

function AppRoutes() {
  const caseApi = useCaseApi();
  const featureFlags = useFeatureFlags();

  // use CaseApi for loaders & actions in the routes

  // The case list loads the full set once and sorts/filters/paginates
  // client-side (see CasesList), so the loader just fetches every case.
  async function casesLoader(): Promise<CasesData> {
    const [cases, accessNames] = await Promise.all([
      caseApi.getCasesList(),
      caseApi.queryAccessNames(),
    ]);
    return { cases, accessNames };
  }

  // the slim list of the cases, and the figures of the portfolio when they come
  async function homeLoader(): Promise<HomeData> {
    const overview = caseApi.getPortfolioOverview();
    // a start page without cases shows no portfolio, so nothing awaits the
    // figures there: their failure is no unhandled rejection
    overview.catch(() => undefined);
    return { cases: await caseApi.getCasesList(), overview };
  }

  // the portfolio filter is shared by the portfolio-stats and settlement loaders
  function portfolioFilterFromRequest(request: Request): CaseFilter {
    const url = new URL(request.url);
    const claimTypeFilter = url.searchParams.getAll("claimType");
    const agreementTypes: AgreementType[] = url.searchParams
      .getAll("agreementType")
      .filter(isAgreementType);
    const settlementTypes: SettlementType[] = url.searchParams
      .getAll("settlement")
      .filter(isSettlementType);

    return {
      claims: {
        types: claimTypeFilter.length > 0 ? claimTypeFilter : undefined,
      },
      agreements: agreementTypes.length > 0 ? agreementTypes : undefined,
      settlements: settlementTypes.length > 0 ? settlementTypes : undefined,
      ...(url.searchParams.get("results") === "adjusted" && {
        settlementAdjusted: true,
      }),
    };
  }

  async function portfolioStatsLoader({
    request,
  }: {
    request: Request;
  }): Promise<PortfolioData> {
    const filter = portfolioFilterFromRequest(request);
    const [stats, caseCounts] = await Promise.all([
      caseApi.getPortfolioStats(filter),
      caseApi.getCaseCounts(filter),
    ]);

    return {
      caseCounts,
      stats,
    };
  }

  // Raw per-item settlement arrays for the settlement sub-area's charts,
  // loaded only when /portfolio/settlements is opened (kept off portfolioStats).
  async function settlementPortfolioLoader({
    request,
  }: {
    request: Request;
  }): Promise<SettlementPortfolio> {
    // the settlement filter is not offered in this section, so a settlement
    // param reaching it (e.g. from a bookmarked URL) is dropped from the URL
    const url = new URL(request.url);
    if (url.searchParams.has("settlement")) {
      url.searchParams.delete("settlement");
      throw redirect(url.pathname + url.search);
    }
    return caseApi.getSettlementPortfolio(portfolioFilterFromRequest(request));
  }

  async function caseLoader({
    params: { caseId },
  }: {
    params: { caseId?: string };
  }) {
    if (caseId === undefined) return;
    const [c, accessNames] = await Promise.all([
      caseApi.queryCase(caseId),
      caseApi.queryAccessNames(),
    ]);
    const data: CaseData = {
      c,
      readOnly: !canWrite(c.accessList, accessNames),
    };
    return data;
  }

  async function addCase({ request }: { request: Request }): Promise<Response> {
    const formData = await request.formData();
    const caseName = getString(formData, "name");
    const caseId = randomId();
    return caseApi.addCase({ id: caseId, name: caseName }).then((c) =>
      // id may get rewritten by server
      redirect(`/cases/${c.id}`),
    );
  }

  async function deleteCase({ params }: { params: { caseId?: string } }) {
    if (params.caseId === undefined) throw "Case ID missing";
    await caseApi.removeCase(params.caseId);
    return redirect(`/cases`);
  }

  async function setTitle({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    const title: string = getString(formData, "title");
    const claimKind = formData.get("claimKind");
    return caseApi.setTitle(
      params.caseId,
      title,
      claimKind === "thirdPartyLiability" || claimKind === "policyholderDispute"
        ? claimKind
        : undefined,
    );
  }

  async function updateParty({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT") {
      const update: Party = JSON.parse(getString(formData, "party"));
      // the cost apportionments page saves the party's costs along
      if (formData.has("budget"))
        return caseApi.updateApportionment(
          params.caseId,
          update,
          JSON.parse(getString(formData, "budget")),
        );
      return caseApi.updateParty(params.caseId, update);
    }
    if (request.method === "POST") {
      if (formData.has("party"))
        return caseApi.addParty(
          params.caseId,
          JSON.parse(getString(formData, "party")),
        );
      return caseApi.addParty(params.caseId, {
        id: getString(formData, "partyId"),
        name: getString(formData, "name"),
        client: formData.get("client") === "true",
      });
    }
    if (request.method === "DELETE")
      return caseApi.removeParty(params.caseId, getString(formData, "partyId"));
    console.warn("Unknown request method", request);
  }

  async function updateClaims({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT") {
      const claim: Claim = JSON.parse(getString(formData, "claim"));
      // a damage an amount of the claim newly claims, or renames, comes along
      const damage = formData.get("damage");
      return caseApi.updateClaim(
        params.caseId,
        claim,
        typeof damage === "string" ? (JSON.parse(damage) as Damage) : undefined,
      );
    }
    if (request.method === "POST") {
      const claim: Claim = JSON.parse(getString(formData, "claim"));
      return caseApi.addClaim(params.caseId, claim);
    }
    if (request.method === "DELETE")
      return caseApi.removeClaim(params.caseId, getString(formData, "claimId"));
    console.warn("Unknown request method", request);
  }

  async function updateSettlementOffers({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT" && formData.has("settlementChances"))
      return caseApi.updateSettlementChances(
        params.caseId,
        JSON.parse(getString(formData, "settlementChances")),
      );
    if (request.method === "PUT") {
      const offer: SettlementOffer = JSON.parse(getString(formData, "offer"));
      return caseApi.updateOffer(params.caseId, offer);
    }
    if (request.method === "POST") {
      const offer: SettlementOffer = JSON.parse(getString(formData, "offer"));
      return caseApi.addOffer(params.caseId, offer);
    }
    if (request.method === "DELETE")
      return caseApi.removeOffer(params.caseId, getString(formData, "offerId"));
    console.warn("Unknown request method", request);
  }

  async function updateBudgets({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT") {
      // the costs page closes and reopens the stages of the timeline too
      if (formData.has("timeline")) {
        const timeline = JSON.parse(getString(formData, "timeline"));
        return formData.has("budgets")
          ? caseApi.closeStage(
              params.caseId,
              timeline,
              JSON.parse(getString(formData, "budgets")),
              closingRecord(formData),
            )
          : caseApi.updateTimeline(params.caseId, timeline);
      }
      // the costs page also sets a party's VAT rate
      if (formData.has("party"))
        return caseApi.updateParty(
          params.caseId,
          JSON.parse(getString(formData, "party")),
        );
      const budget: Budget = JSON.parse(getString(formData, "budget"));
      return caseApi.updateBudget(params.caseId, budget);
    }
    console.warn("Unsupported request method", request);
  }

  async function updateBookedReserves({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT")
      return caseApi.updateBookedReserves(
        params.caseId,
        JSON.parse(getString(formData, "bookedReserves")),
      );
    console.warn("Unsupported request method", request);
  }

  async function updateTimeline({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT") {
      const timeline = JSON.parse(getString(formData, "timeline"));
      // closing a stage saves what was spent on it along
      if (formData.has("budgets"))
        return caseApi.closeStage(
          params.caseId,
          timeline,
          JSON.parse(getString(formData, "budgets")),
          closingRecord(formData),
        );
      return caseApi.updateTimeline(params.caseId, timeline);
    }
    console.warn("Unsupported request method", request);
  }

  async function updateCostStructure({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT") {
      const costStructure: CostStructure = JSON.parse(
        getString(formData, "costStructure"),
      );
      return caseApi.updateCostStructure(params.caseId, costStructure);
    }
    console.warn("Unsupported request method", request);
  }

  async function setBargainingVariant({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT" || request.method === "POST") {
      const variant: Configuration = JSON.parse(getString(formData, "variant"));
      await caseApi.setBargainingVariant(params.caseId, variant);
      return "ok";
    }
    console.warn("Unsupported request method", request);
  }

  async function setPartyAgreements({
    request,
    params,
  }: {
    request: Request;
    params: { caseId?: string };
  }) {
    if (params.caseId === undefined) throw "Case ID missing";
    const formData = await request.formData();
    if (request.method === "PUT" || request.method === "POST") {
      const partyAgreements: PartyAgreements = JSON.parse(
        getString(formData, "partyAgreements"),
      );
      return caseApi
        .setPartyAgreements(params.caseId, partyAgreements)
        .then(() => "ok");
    }
    console.warn("Unsupported request method", request);
  }

  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <CurrencyContextProvider>
          <LoggedInPage />
        </CurrencyContextProvider>
      ),
      children: [
        {
          path: "/addCase",
          element: <CaseCreator />,
          action: addCase,
        },
        {
          path: "/",
          element: <Home />,
          // the start page counts and lists the cases from the same slim list
          // as the page of the cases loads, and shows the portfolio's figures
          loader: homeLoader,
          // on the first load, the bar above stays and the page shows its
          // skeleton until its loader has run, as on a navigation to it
          hydrateFallbackElement: <RouterSkeleton />,
        },
        {
          path: "/profile",
          element: <UserProfile />,
        },
        {
          path: "/portfolio",
          element: (
            <PortfolioProvider>
              <PortfolioPage />
            </PortfolioProvider>
          ),
          loader: portfolioStatsLoader,
          hydrateFallbackElement: <RouterSkeleton />,
          children: [
            {
              path: "firm",
              element: <LawFirmPortfolio />,
            },
            {
              path: "claims",
              element: <ClaimsPortfolio />,
            },
            {
              path: "clients",
              element: <ClientsPortfolio />,
            },
            {
              path: "settlements",
              element: <SettlementsPortfolio />,
              loader: settlementPortfolioLoader,
            },
          ],
        },
        {
          path: "/testimonials",
          element: <Testimonials />,
        },
        {
          path: "/cases",
          element: <GetCases />,
          loader: casesLoader,
          hydrateFallbackElement: <RouterSkeleton />,
        },
        {
          id: CASE_ROUTE_ID,
          path: "/cases/:caseId/",
          element: (
            <CaseContextProvider>
              <ResultsViewProvider>
                <CasePage />
              </ResultsViewProvider>
            </CaseContextProvider>
          ),
          loader: caseLoader,
          hydrateFallbackElement: <RouterSkeleton />,
          // The rename action lives on this layout route (not the index child)
          // because useCaseSubmit posts to the bare pathname `/cases/:caseId`.
          // React Router only targets an index route's action when the URL
          // carries a `?index` query; without it the submission resolves to the
          // deepest path-contributing match — this layout route.
          action: withAbortHandling(setTitle),
          children: [
            {
              index: true,
              element: (
                <>
                  <ResultsSwitch />
                  <ShownResults>
                    <CaseOverview />
                  </ShownResults>
                </>
              ),
            },
            {
              path: "delete",
              action: deleteCase,
            },
            {
              path: "configuration",
              children: [
                {
                  path: "parties",
                  element: (
                    <FrozenByJudgment>
                      <Parties />
                    </FrozenByJudgment>
                  ),
                  action: withAbortHandling(updateParty),
                },
                {
                  path: "timeline",
                  element: <TimelinePage />,
                  action: withAbortHandling(updateTimeline),
                },
                {
                  path: "claims",
                  element: (
                    <FrozenByJudgment>
                      <Claims />
                    </FrozenByJudgment>
                  ),
                  action: withAbortHandling(updateClaims),
                },
                {
                  path: "costs",
                  element: <Budgeting />,
                  action: withAbortHandling(updateBudgets),
                },
                {
                  path: "agreements",
                  element: <AgreementsPage />,
                  action: withAbortHandling(setPartyAgreements),
                },
                {
                  path: "apportionments",
                  element: <CostApportionments />,
                  action: withAbortHandling(updateParty),
                },
                {
                  path: "costStructure",
                  element: <LawFirmFees />,
                  action: withAbortHandling(updateCostStructure),
                },
                {
                  path: "recoveries",
                  element: <CostRecoveries />,
                  action: withAbortHandling(updateParty),
                },
                {
                  path: "settlement",
                  element: (
                    <FrozenByJudgment>
                      <SettlementAnalytics />
                    </FrozenByJudgment>
                  ),
                  action: withAbortHandling(updateSettlementOffers),
                },
              ],
            },
            // where the settlements were before they became configuration
            {
              path: "analytics/settlement",
              element: (
                <Navigate
                  to="../../configuration/settlement"
                  relative="path"
                  replace
                />
              ),
            },
            {
              path: "analytics",
              // one gate for every analytics view: an incomplete case has no
              // calculated results, so show what's missing rather than seven
              // different "no data" messages
              element: <AnalyticsGate />,
              children: [
                {
                  path: "value",
                  element: <EvAnalytics />,
                },
                {
                  path: "netResults",
                  element: <NetResultAnalytics />,
                },
                {
                  path: "risk",
                  element: <RiskAnalytics />,
                },
                {
                  path: "reserve",
                  element: <ReservePage />,
                  action: withAbortHandling(updateBookedReserves),
                },
                {
                  path: "bargaining",
                  element: <BargainingAnalytics />,
                  action: withAbortHandling(setBargainingVariant),
                },
                // {
                //   path: "sensitivity",
                //   element: <SensitivityAnalysis />,
                // },
                {
                  path: "revenue",
                  element: <RevenueAnalytics />,
                },
                {
                  path: "outcomes",
                  element: <CaseOutcomes />,
                },
                {
                  path: "tree",
                  element: featureFlags?.decisionTree && <DecisionTree />,
                },
                // {
                //   path: "var",
                //   element: <ValueAtRiskAnalytics />,
                // },
              ],
            },
          ],
        },
      ],
      errorElement: <ErrorPage />,
    },
  ]);

  return <RouterProvider router={router} />;
}

function App() {
  return (
    <ParseAuthProvider>
      <ThemeContextProvider>
        <CssBaseline />
        <FeatureFlagsProvider>
          <PersonaTerms>
            <AuthCheck />
          </PersonaTerms>
        </FeatureFlagsProvider>
      </ThemeContextProvider>
    </ParseAuthProvider>
  );
}

export default App;
